import { CSSProperties, SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { elementService } from 'bom/services';
import { IElement, IElementAutocompleteRequestPayload } from 'bom/types';
import { FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { UpsertElement } from 'jobs/components';

interface IProps {
  jobId?: number;
  name?: string;
  label?: string | null;
  placeholder?: string;
  requestPayload?: IElementAutocompleteRequestPayload;
  onChange?: (element: IElement | null) => void;
  onAddNewElement?: (element: Partial<IElement> & IAutocompleteOption) => void;
  onSubmitSuccessAddNewElement?: (element: IElement) => void;
  onCloseAddNewElement?: () => void;
  required?: boolean;
  disabled?: boolean;
  wrapperStyle?: CSSProperties;
}

export const ElementFormField = ({
  jobId = null,
  name = 'element',
  label,
  placeholder,
  requestPayload: customRequestPayload,
  onChange,
  onSubmitSuccessAddNewElement,
  onCloseAddNewElement,
  required = false,
  disabled = false,
  wrapperStyle,
}: IProps) => {
  const [newElement, setNewElement] = useState<Partial<IElement> | null>(null);

  const { t } = useTranslation();

  const { control, errors, setValue } = useFormContext();

  const requestPayload = useMemo(() => {
    return {
      ...customRequestPayload,
      filters: {
        empty: 0,
        ...customRequestPayload?.filters,
      },
    };
  }, [customRequestPayload]);

  const handleOnAddNewElement = (option: IElement & IAutocompleteOption) => {
    const newElement: Partial<IElement> = {
      id: null,
      name: option.title,
    };

    setValue(name, newElement);

    if (jobId) {
      setNewElement(newElement);
    }
  };

  const handleOnChange = (_: SyntheticEvent, option: IElement | null) => {
    if (onChange) {
      onChange(option);
    }
  };

  const handleOnSubmitSuccessAddNewElement = (element: IElement) => {
    handleOnChange(null, element);

    if (onSubmitSuccessAddNewElement) {
      onSubmitSuccessAddNewElement(element);
    }
  };

  const handleOnCloseAddNewElement = () => {
    setNewElement(null);
    handleOnChange(null, null);

    onCloseAddNewElement?.();
  };

  return (
    <>
      <FormField
        name={name}
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          control,
          error: errors?.[name],
          optionKey: 'name',
          ...(label !== null && { label: label || t('labelElement') }),
          placeholder: placeholder || t('placeholderSelectElement'),
          getOptions: elementService.getElementsAutocomplete,
          onNewOption: handleOnAddNewElement,
          onChange: handleOnChange,
          requestPayload,
          required,
          disabled,
          enabledOnFocus: false,
          hasCreateOption: true,
        }}
        wrapperStyle={wrapperStyle}
      />

      {jobId && (
        <UpsertElement
          jobId={jobId}
          defaultValues={{
            element: newElement,
          }}
          isOpen={!!newElement}
          onClose={handleOnCloseAddNewElement}
          onSubmitSuccess={handleOnSubmitSuccessAddNewElement}
        />
      )}
    </>
  );
};
